// Planning de toute l'équipe, présenté comme le tableau Excel affiché en magasin :
// une ligne par personne (dans l'ordre de l'Excel, avec sa couleur), une colonne par jour.
import { Fragment, useEffect, useState } from 'react'
import { format, addDays, startOfWeek } from 'date-fns'
import { fr } from 'date-fns/locale'
import MobileLayout from '../../components/MobileLayout'
import { employeeApi } from '../../lib/employeeApi'

interface Slot { start: string; end: string }
interface Row { id: string; name: string; initial: string; color: string | null; days: Record<string, Slot[]> }
interface Week { weekStart: string; days: string[]; rows: Row[]; me: string }

const hours = (list: Slot[]) => list.reduce((sum, s) => {
  const [sh, sm] = s.start.split(':').map(Number)
  const [eh, em] = s.end.split(':').map(Number)
  return sum + ((eh * 60 + em) - (sh * 60 + sm)) / 60
}, 0)

export default function MobileTeamPlanning() {
  const [monday, setMonday] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }))
  const [week, setWeek] = useState<Week | null>(null)
  const [loading, setLoading] = useState(true)
  const today = format(new Date(), 'yyyy-MM-dd')

  useEffect(() => {
    setLoading(true)
    employeeApi.get('/employee/me/team-week', { params: { weekStart: format(monday, 'yyyy-MM-dd') } })
      .then(r => setWeek(r.data))
      .catch(() => setWeek(null))
      .finally(() => setLoading(false))
  }, [monday])

  // Prénom seul, initiale ajoutée si deux personnes ont le même prénom
  const label = (r: Row) => {
    const same = (week?.rows || []).filter(x => x.name.toLowerCase() === r.name.toLowerCase()).length > 1
    return same && r.initial ? `${r.name} ${r.initial}.` : r.name
  }

  return (
    <MobileLayout>
      {/* Largeur bornée à l'écran : seul le tableau défile de gauche à droite */}
      <div className="p-3 space-y-3 w-screen max-w-full lg:w-auto overflow-hidden">
        {/* Semaine */}
        <div className="flex items-center justify-between bg-white rounded-xl shadow-sm px-2 py-1.5">
          <button onClick={() => setMonday(d => addDays(d, -7))} className="px-3 py-2 text-lg text-gray-600">‹</button>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-800">
              {format(monday, 'd MMM', { locale: fr })} – {format(addDays(monday, 6), 'd MMM yyyy', { locale: fr })}
            </p>
            <button onClick={() => setMonday(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="text-xs text-blue-600">Cette semaine</button>
          </div>
          <button onClick={() => setMonday(d => addDays(d, 7))} className="px-3 py-2 text-lg text-gray-600">›</button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-400">Chargement…</div>
        ) : !week || week.rows.length === 0 ? (
          <div className="text-center py-10 text-gray-400 bg-white rounded-xl">Pas de planning pour cette semaine</div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm overflow-x-auto border-2 border-gray-900 max-w-full">
              <table className="border-collapse text-[10.5px] md:text-sm leading-tight w-full">
                <thead>
                  <tr>
                    <th rowSpan={2} className="sticky left-0 z-10 bg-gray-900 text-white px-2 py-2 text-left min-w-[70px]">Équipe</th>
                    {week.days.map(d => (
                      <th key={d} colSpan={2} className={`px-1 py-1 border-l-[3px] border-gray-900 ${d === today ? 'bg-blue-600 text-white' : 'bg-gray-900 text-white'}`}>
                        <span className="uppercase">{format(new Date(d + 'T12:00:00'), 'EEE', { locale: fr })}</span>{' '}
                        <span className="text-sm font-bold">{format(new Date(d + 'T12:00:00'), 'd')}</span>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {week.days.map(d => (
                      <Fragment key={d}>
                        <th className="px-1 py-0.5 min-w-[46px] bg-amber-100 text-amber-900 font-semibold border-l-[3px] border-gray-900">Matin</th>
                        <th className="px-1 py-0.5 min-w-[46px] bg-indigo-100 text-indigo-900 font-semibold border-l border-gray-400">A-M</th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {week.rows.map(r => {
                    const isMe = r.id === week.me
                    const total = Object.values(r.days).reduce((a, l) => a + hours(l), 0)
                    return (
                      <tr key={r.id} style={{ backgroundColor: r.color || undefined }} className="border-t border-gray-900/30">
                        <td className={`sticky left-0 z-10 px-2 py-1.5 font-bold text-gray-900 ${isMe ? 'outline outline-2 outline-blue-600 -outline-offset-2' : ''}`}
                          style={{ backgroundColor: r.color || '#fff' }}>
                          <div className="truncate max-w-[72px]">{label(r)}</div>
                          <div className="text-[10px] font-normal opacity-70">{total ? `${total.toFixed(1)}h` : ''}</div>
                        </td>
                        {week.days.map(d => {
                          const list = r.days[d] || []
                          // Matin = horaire qui commence avant midi (comme les colonnes du tableau Excel)
                          const am = list.filter(s => s.start < '12:00')
                          const pm = list.filter(s => s.start >= '12:00')
                          const cell = (slots: Slot[]) => slots.length === 0
                            ? <span className="text-gray-900/30">—</span>
                            : slots.map((s, i) => (
                              <div key={i} className="font-semibold text-gray-900 whitespace-nowrap">
                                <div>{s.start}</div><div>{s.end}</div>
                              </div>
                            ))
                          return (
                            <Fragment key={d}>
                              <td className={`px-1 py-1 text-center align-middle border-l-[3px] border-gray-900 ${d === today ? 'ring-1 ring-inset ring-blue-500/40' : ''}`}>{cell(am)}</td>
                              <td className={`px-1 py-1 text-center align-middle border-l border-gray-900/30 ${d === today ? 'ring-1 ring-inset ring-blue-500/40' : ''}`}>{cell(pm)}</td>
                            </Fragment>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <p className="md:hidden text-xs text-gray-400 text-center">Faites glisser le tableau vers la gauche pour voir toute la semaine</p>
          </>
        )}
      </div>
    </MobileLayout>
  )
}
